import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UMFiltersPanel, UMLayersPanel, UMMap, UMMapControls, UMMessage, UM_LAYER_CATALOG, type UMFilterValues, type UMMapHandle } from '../../urbanmap/_components';
import { BuildingCard } from './BuildingCard';
import { MapSearchBar } from './MapSearchBar';
import { DevelopmentPlansPanel } from './DevelopmentPlansPanel';
import { boundsOf, buildingConsumption } from './domain';
import { useProject, useUpdateProject } from './hooks';
import { EMPTY, buildingsFC, projectLayers, ringFC } from './mapLayers';
import { ProjectHeader, ProjectRail } from './ProjectHeader';
import { useProjectSelection } from './ProjectSelection';
import { SettingsPanel } from './SettingsPanel';
import { same, toDraft, toUpdate, type SettingsDraft } from './configDraft';
import { SyncPanel } from './SyncPanel';
import { planBuildings, planRoute, routeFC } from './planGeometry';
import { useDevelopmentPlans } from './plans.hooks';
import type { LngLat, PlanBuilding, Referential } from './types';

interface Props {
  projectId: string;
  referential: Referential;
  onBack: () => void;
  flash: (msg: string) => void;
}

export function ProjectView({ projectId, referential, onBack, flash }: Props) {
  const { data, isLoading, isError } = useProject(projectId);
  const update = useUpdateProject(projectId);
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<'settings' | 'sync' | 'plans' | 'filters' | 'selection' | 'layers' | null>(null);
  const [filters, setFilters] = useState<UMFilterValues>({});
  const [layersOn, setLayersOn] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(UM_LAYER_CATALOG.map((g) => [g.id, !!g.defaultChecked])));
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [revision, setRevision] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [shownPlanId, setShownPlanId] = useState<string | null>(null);
  const plans = useDevelopmentPlans(projectId);
  const shownPlan = shownPlanId ? plans.data?.find((p) => p.id === shownPlanId) ?? null : null;

  const project = data?.project;
  const saved = useMemo(() => (project ? toDraft(project) : null), [project]);
  const dirty = !!(draft && saved && !same(draft, saved));
  const params = panel === 'settings' && draft ? draft : saved;
  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const changedIds = useMemo(() => new Set(data?.umChangedIds ?? []), [data]);

  const outlines: LngLat[][] = useMemo(() => {
    if (!project) return [];
    if (project.perimeter.kind === 'drawn') return [project.perimeter.polygon];
    return project.perimeter.communes.map((c) => referential.communeOutlines[c]).filter((r): r is LngLat[] => !!r);
  }, [project, referential.communeOutlines]);

  const planB = useMemo(() => (shownPlan ? planBuildings(shownPlan, buildings) : []), [shownPlan, buildings]);
  const planIds = useMemo(() => new Set(planB.map((b) => b.id)), [planB]);
  const routed = !!shownPlan && shownPlan.completedSteps >= 2;
  const sources = useMemo(() => ({
    buildings: params ? buildingsFC(buildings, (b) => ({
      conso: Math.round(buildingConsumption(b, params.ratios, params.dju).corrected * 10) / 10, plan: planIds.has(b.id) ? 1 : 0,
    })) : EMPTY,
    outline: ringFC(outlines),
    'plan-route': routed ? routeFC(planRoute(planB)) : EMPTY,
  }), [buildings, params, outlines, planIds, planB, routed]);
  useEffect(() => {
    if (planB.length) mapRef.current?.getMap()?.fitBounds(boundsOf(planB.map((b) => b.centroid)), { padding: 80, maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recentrage à l'ouverture d'un plan ou quand ses bâtiments changent
  }, [shownPlanId, planB.length]);
  const pickedB = picked ? buildings.find((b) => b.id === picked) ?? null : null;
  const heatOf = useCallback((b: PlanBuilding) => (saved ? buildingConsumption(b, saved.ratios, saved.dju).corrected : 0), [saved]);
  const sel = useProjectSelection(mapRef, buildings, heatOf, () => setPanel('selection'), flash);
  const cancelNetwork = sel.network.cancel;
  useEffect(() => { if (panel !== 'selection') cancelNetwork(); }, [panel, cancelNetwork]);
  const layers = useMemo(() => {
    const hide = layersOn.buildings === false;
    const base = projectLayers(pickedB?.num ?? null, planIds.size > 0).map((l) => (hide && 'source' in l && l.source === 'buildings'
      ? { ...l, layout: { ...('layout' in l ? l.layout : {}), visibility: 'none' as const } } : l));
    return [...base, ...sel.layers];
  }, [pickedB, sel.layers, layersOn.buildings, planIds]);

  if (isLoading) return <div style={{ padding: 24 }}>Chargement du projet…</div>;
  if (isError || !data || !project || !params || !saved) {
    return <div style={{ padding: 24 }}>Projet introuvable. <button type="button" onClick={onBack}>Retour aux projets</button></div>;
  }

  const initialBounds = outlines.length ? boundsOf(outlines.flat()) : referential.bounds;
  const openSettings = () => {
    if (panel === 'settings') { closeSettings(); return; }
    setDraft(toDraft(project)); setJustSaved(false); setRevision((r) => r + 1); setPanel('settings');
  };
  const closeSettings = () => {
    if (update.isPending) return;
    if (dirty) flash('Modifications non enregistrées abandonnées');
    setPanel(null); setDraft(null);
  };
  const save = () => {
    if (!draft) return;
    update.mutate(toUpdate(draft), {
      onSuccess: (d) => { setDraft(toDraft(d.project)); setJustSaved(true); flash('Paramètres enregistrés · consommations recalculées'); },
      onError: () => flash('Échec de l’enregistrement, réessayez'),
    });
  };

  return (
    <div data-comment-view={`project-${projectId}`} style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <ProjectRail flash={flash} onSettings={openSettings} settingsOpen={panel === 'settings'}
        plansOpen={panel === 'plans'}
        onPlans={() => { if (panel === 'settings') closeSettings(); setPanel((p) => (p === 'plans' ? null : 'plans')); }}
        filtersOpen={panel === 'filters'}
        onFilters={() => { if (panel === 'settings') closeSettings(); setPanel((p) => (p === 'filters' ? null : 'filters')); }}
        selectionOpen={panel === 'selection'}
        layersOpen={panel === 'layers'}
        onLayers={() => { if (panel === 'settings') closeSettings(); if (panel === 'selection') sel.close(); setPanel((p) => (p === 'layers' ? null : 'layers')); }}
        onSelection={() => { if (panel === 'settings') closeSettings(); if (panel === 'selection') sel.close(); setPanel((p) => (p === 'selection' ? null : 'selection')); }}
        onProjects={() => { if (dirty) flash('Modifications non enregistrées abandonnées'); onBack(); }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <ProjectHeader
          project={project}
          umChanges={data.umChanges}
          syncOpen={panel === 'sync'}
          onSync={() => { if (panel === 'settings') closeSettings(); setPanel(panel === 'sync' ? null : 'sync'); }}
          planName={shownPlan?.name}
          onProjectRoot={() => setShownPlanId(null)}
        />
        <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
          {panel === 'plans' && <DevelopmentPlansPanel projectId={projectId} onClose={() => setPanel(null)} flash={flash} selected={shownPlanId} onSelect={setShownPlanId} />}
          {panel === 'filters' && <UMFiltersPanel values={filters} onChange={setFilters} onClose={() => setPanel(null)} />}
          {panel === 'layers' && <UMLayersPanel onClose={() => setPanel(null)} checked={layersOn}
            onCheckedChange={(id, value) => setLayersOn((prev) => ({ ...prev, [id]: value }))} />}
          {panel === 'selection' && sel.panel(() => setPanel(null))}
          <main data-comment-anchor="project-map" style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <UMMap
              ref={mapRef}
              sources={{ ...sources, 'selection-network': sel.source }}
              layers={layers}
              initialBounds={initialBounds}
              interactiveLayerIds={['pc-prj-building']}
              onFeatureClick={(_, f) => { if (!sel.editing && !sel.network.loading) setPicked(String(f.properties?.fid)); }}
              onMapClick={() => setPicked(null)}
            />
            <MapSearchBar mapRef={mapRef} communes={referential.communes} communeOutlines={referential.communeOutlines} home={outlines.flat()} />
            {sel.instruction && (
              <div role="status" aria-live="polite" style={{ position: 'absolute', top: 64, left: 16, right: 16, zIndex: 30, maxWidth: 520 }}>
                <UMMessage title="Prototype — outil réseau">{`${sel.instruction} Annuler ou Échap abandonne le brouillon.`}</UMMessage>
              </div>
            )}
            <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
          </main>
          {pickedB && (
            <BuildingCard building={pickedB} ratios={params.ratios} dju={params.dju} changedInUM={changedIds.has(pickedB.id)} preview={panel === 'settings' && dirty} onClose={() => setPicked(null)} />
          )}
          {panel === 'settings' && draft && (
            <div data-comment-view="panel-settings" style={{ display: 'contents' }}>
            <SettingsPanel
              project={project}
              buildings={buildings}
              referential={referential}
              draft={draft}
              onDraftChange={(p) => { setJustSaved(false); setDraft((d) => (d ? { ...d, ...p } : d)); }}
              dirty={dirty}
              saving={update.isPending}
              justSaved={justSaved}
              saveError={update.isError}
              revision={revision}
              onSave={save}
              onCancel={() => { update.reset(); setJustSaved(false); setDraft(toDraft(project)); setRevision((r) => r + 1); }}
              onClose={closeSettings}
            />
            </div>
          )}
          {panel === 'sync' && (
            <div data-comment-view="panel-sync" style={{ display: 'contents' }}>
              <SyncPanel projectId={projectId} onClose={() => setPanel(null)} onDone={flash} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
